﻿// Commodity metadata and categorization

export interface CommodityMeta {
    key: string;
    label: string;
    labelMm: string; // Added Myanmar Label
    unit: string;
    category:
    | 'essentials'
    | 'food'
    | 'protein'
    | 'financial'
    | 'retail_staples'
    | 'retail_bakery_dairy'
    | 'retail_instant'
    | 'retail_condiments'
    | 'retail_meat_seafood'
    | 'retail_fruit_veg'
    | 'retail_pulses'
    | 'retail_ingredients';
    icon?: string;
}

export const COMMODITY_CATEGORIES = {
    essentials: {
        label: 'Essentials',
        labelMm: 'အခြေခံ',
        icon: '⚡',
        description: 'Fuel, Rice and essential staples',
    },
    food: {
        label: 'Food and Cooking',
        labelMm: 'စားသောက်ကုန်',
        icon: '🌾',
        description: 'Oils, pulses, spices',
    },
    protein: {
        label: 'Protein',
        labelMm: 'အသားငါး',
        icon: '🍖',
        description: 'Meat, fish, poultry',
    },
    financial: {
        label: 'Financial',
        labelMm: 'ငွေကြေး',
        icon: '💰',
        description: 'Currency and gold',
    },

    // Retail (Citymall)
    retail_staples: {
        label: 'Retail: Staples',
        labelMm: 'လက်လီ: အခြေခံ',
        icon: '🛒',
        description: 'Rice, oils, sugar, salt',
    },
    retail_bakery_dairy: {
        label: 'Retail: Bakery and Dairy',
        labelMm: 'လက်လီ: မုန့်နှင့်နို့',
        icon: '🥛',
        description: 'Milk and bread',
    },
    retail_instant: {
        label: 'Retail: Instant Foods',
        labelMm: 'လက်လီ: အသင့်စား',
        icon: '🍜',
        description: 'Instant noodles and ready-to-eat',
    },
    retail_condiments: {
        label: 'Retail: Condiments',
        labelMm: 'လက်လီ: အမွှေးအကြိုင်',
        icon: '🧂',
        description: 'Soy sauce, fish sauce, seasonings',
    },
    retail_meat_seafood: {
        label: 'Retail: Meat & Seafood',
        labelMm: 'လက်လီ: အသားငါး',
        icon: '🥩',
        description: 'Chicken, Pork, Fish, Prawns',
    },
    retail_fruit_veg: {
        label: 'Retail: Fruit & Veg',
        labelMm: 'လက်လီ: သစ်သီးဝလံ',
        icon: '🥦',
        description: 'Fresh vegetables and fruits',
    },
    retail_pulses: {
        label: 'Retail: Pulses',
        labelMm: 'လက်လီ: ပဲမျိုးစုံ',
        icon: '🥜',
        description: 'Beans and pulses',
    },
    retail_ingredients: {
        label: 'Retail: Ingredients',
        labelMm: 'လက်လီ: ပါဝင်ပစ္စည်းများ',
        icon: '🥘',
        description: 'Cooking ingredients',
    },
};

export const COMMODITIES: CommodityMeta[] = [
    // Essentials
    { key: 'fuel_92_octane', label: 'Fuel (92 Octane)', labelMm: 'စက်သုံးဆီ (၉၂)', unit: 'MMK/L', category: 'essentials' },
    { key: 'rice', label: 'Rice (Emata Medium)', labelMm: 'ဧည့်မထ (လတ်)', unit: 'MMK/kg', category: 'essentials' },
    { key: 'rice_shwebo', label: 'Rice (ShweBo Pawsan)', labelMm: 'ရွှေဘိုပေါ်ဆန်း', unit: 'MMK/kg', category: 'essentials' },

    // Food and cooking - Oils
    { key: 'palm_oil', label: 'Palm Oil', labelMm: 'စားအုန်းဆီ', unit: 'MMK/viss', category: 'food' },
    { key: 'groundnut_oil', label: 'Groundnut Oil (Special)', labelMm: 'မြေပဲဆီ (ရှယ်)', unit: 'MMK/viss', category: 'food' },
    { key: 'sesamum_oil', label: 'Sesamum Oil', labelMm: 'နှမ်းဆီ', unit: 'MMK/viss', category: 'food' },

    // Food and cooking - Pulses
    { key: 'gram', label: 'Gram', labelMm: 'ကုလားပဲ', unit: 'MMK/viss', category: 'food' },
    { key: 'penilay', label: 'Penilay', labelMm: 'ပဲနီလေး', unit: 'MMK/viss', category: 'food' },
    { key: 'pegyi', label: 'Pegyi', labelMm: 'ပဲကြီး', unit: 'MMK/viss', category: 'food' },
    { key: 'satawpe', label: 'Satawpe', labelMm: 'စားတော်ပဲ', unit: 'MMK/viss', category: 'food' },

    // Food and cooking - Spices
    { key: 'dried_chillies', label: 'Dried Chillies (Long)', labelMm: 'ငရုတ်ခြောက် (ရှည်)', unit: 'MMK/viss', category: 'food' },
    { key: 'garlic', label: 'Garlic (Shan Hmwar)', labelMm: 'ကြက်သွန်ဖြူ (ရှမ်းမွှာ)', unit: 'MMK/viss', category: 'food' },
    { key: 'onion', label: 'Onion (Seik Phyu)', labelMm: 'ကြက်သွန်နီ (ဆိပ်ဖြူ)', unit: 'MMK/viss', category: 'food' },
    { key: 'potato', label: 'Potato (Medium)', labelMm: 'အာလူး (လတ်)', unit: 'MMK/viss', category: 'food' },

    // Protein
    { key: 'chicken', label: 'Chicken (Slice)', labelMm: 'ကြက်သား (အသား)', unit: 'MMK/viss', category: 'protein' },
    { key: 'pork', label: 'Pork (Belly)', labelMm: 'ဝက်သား (သုံးထပ်သား)', unit: 'MMK/viss', category: 'protein' },
    { key: 'beef', label: 'Beef (Without bone)', labelMm: 'အမဲသား (အသား)', unit: 'MMK/viss', category: 'protein' },
    { key: 'mutton', label: 'Mutton (Without bone)', labelMm: 'ဆိတ်သား (အသား)', unit: 'MMK/viss', category: 'protein' },
    { key: 'ngakhu', label: 'Ngakhu (Yecho Medium)', labelMm: 'ငါးခူ (ရေချို လတ်)', unit: 'MMK/viss', category: 'protein' },
    { key: 'ngagyi', label: 'Ngagyi (Medium)', labelMm: 'ငါးကြီး (လတ်)', unit: 'MMK/viss', category: 'protein' },
    { key: 'pazunkyawt', label: 'Pazunkyawt (4cm)', labelMm: 'ပုဇွန်ကျော့ (၄ စင်တီ)', unit: 'MMK/viss', category: 'protein' },

    // Financial
    { key: 'usd_buy', label: 'USD (Buy)', labelMm: 'အမေရိကန်ဒေါ်လာ (ဝယ်)', unit: 'MMK', category: 'financial' },
    { key: 'usd_sell', label: 'USD (Sell)', labelMm: 'အမေရိကန်ဒေါ်လာ (ရောင်း)', unit: 'MMK', category: 'financial' },
    { key: 'gold_24k', label: 'Gold (24K)', labelMm: 'ရွှေ (၂၄ ကေ)', unit: 'MMK/tical', category: 'financial' },

    // Retail staples
    { key: 'retail_rice_thant_shwe_bo_5kg', label: 'Thant Shwe Bo Rice (5kg)', labelMm: 'သန့်ရွှေဘိုဆန် (၅ ကီလို)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_rice_nursery_pawsan_5kg', label: 'Nursery Paw San Rice (5kg)', labelMm: 'နားဆာရီပေါ်ဆန်း (၅ ကီလို)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_rice_mya_shwe_bo_12kg', label: 'Mya Shwe Bo Rice (12kg)', labelMm: 'မြရွှေဘိုဆန် (၁၂ ကီလို)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_oil_ayeyarwady_peanut_1l', label: 'Ayeyarwady Peanut Oil (1L)', labelMm: 'ဧရာဝတီမြေပဲဆီ (၁ လီတာ)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_oil_ah_may_htwar_peanut_1l', label: 'Ah May Htwar Peanut Oil (1L)', labelMm: 'အမေ့ထွာမြေပဲဆီ (၁ လီတာ)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_oil_kaung_mha_kaung_soybean_2l', label: 'Kaung Mha Kaung Soybean Oil (2L)', labelMm: 'ကောင်းမှကောင်းပဲပုပ်ဆီ (၂ လီတာ)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_sugar_mitr_phol_white_1kg', label: 'Mitr Phol White Sugar (1kg)', labelMm: 'Mitr Phol သကြားဖြူ (၁ ကီလို)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_salt_white_elephant_400g', label: 'White Elephant Salt (400g)', labelMm: 'ဆင်ဖြူတော်ဆား (၄၀၀ ဂရမ်)', unit: 'Ks', category: 'retail_staples' },
    { key: 'retail_water_drinking_1_5l', label: 'Drinking Water (1.5L)', labelMm: 'သောက်ရေသန့် (၁.၅ လီတာ)', unit: 'Ks', category: 'retail_staples' },

    // Retail bakery and dairy
    { key: 'retail_milk_vito_active_soy_1l', label: 'Vito Active Soy Milk (1L)', labelMm: 'Vito Active ပဲနို့ (၁ လီတာ)', unit: 'Ks', category: 'retail_bakery_dairy' },
    { key: 'retail_eggs_chicken_10pcs', label: 'Chicken Eggs (10pcs)', labelMm: 'ကြက်ဥ (၁၀ လုံး)', unit: 'Ks', category: 'retail_bakery_dairy' },
    { key: 'retail_bread_good_morning_400g', label: 'Good Morning Bread (400g)', labelMm: 'Good Morning ပေါင်မုန့် (၄၀၀ ဂရမ်)', unit: 'Ks', category: 'retail_bakery_dairy' },
    { key: 'retail_bread_moe_slice_10pcs', label: 'Moe Slice Bread (10pcs)', labelMm: 'မိုးပေါင်မုန့် (၁၀ ချပ်)', unit: 'Ks', category: 'retail_bakery_dairy' },

    // Retail instant foods
    { key: 'retail_noodles_wah_lah_shrimp_5pk', label: 'Wah Lah Shrimp Noodles (5pk)', labelMm: 'ဝါးလားပုဇွန်ခေါက်ဆွဲ (၅ ထုပ်)', unit: 'Ks', category: 'retail_instant' },
    { key: 'retail_noodles_yumyum_tomyum_creamy', label: 'Yum Yum Tom Yum Noodles', labelMm: 'ယမ်ယမ်တုန်ယမ်းခေါက်ဆွဲ', unit: 'Ks', category: 'retail_instant' },
    { key: 'retail_noodles_shin_shin_malar_10pcs', label: 'Shin Shin Malar Noodles (10pcs)', labelMm: 'ရှင်းရှင်းမာလာခေါက်ဆွဲ (၁၀ ထုပ်)', unit: 'Ks', category: 'retail_instant' },
    { key: 'retail_noodles_mamee_omg_mala_5pk', label: 'Mamee Omg Mala Noodles (5pk)', labelMm: 'Mamee Omg မာလာခေါက်ဆွဲ (၅ ထုပ်)', unit: 'Ks', category: 'retail_instant' },

    // Retail condiments
    { key: 'retail_soy_sauce_maggi_680ml', label: 'Maggi Soy Sauce (680ml)', labelMm: 'မက်ဂီပဲငံပြာရည် (၆၈၀ မီလီ)', unit: 'Ks', category: 'retail_condiments' },
    { key: 'retail_fish_sauce_min_thar_gyi_160g', label: 'Min Thar Gyi Fish Sauce (160g)', labelMm: 'မင်းသားကြီးငံပြာရည် (၁၆၀ ဂရမ်)', unit: 'Ks', category: 'retail_condiments' },

    // Retail Meat & Seafood
    { key: 'retail_meat_chicken_whole', label: 'Chicken (Whole) ~1.5kg', labelMm: 'ကြက်တစ်ကောင်လုံး (~၁.၅ ကီလို)', unit: 'Ks', category: 'retail_meat_seafood' },
    { key: 'retail_meat_pork_chop_300g', label: 'Pork Chop (300g)', labelMm: 'ဝက်နံရိုး (၃၀၀ ဂရမ်)', unit: 'Ks', category: 'retail_meat_seafood' },
    { key: 'retail_meat_fish_fillet', label: 'Fish Fillet (1 pack)', labelMm: 'ငါးအသားလွှာ (၁ ထုပ်)', unit: 'Ks', category: 'retail_meat_seafood' },
    { key: 'retail_meat_prawn_250g', label: 'Prawns (250g)', labelMm: 'ပုဇွန် (၂၅၀ ဂရမ်)', unit: 'Ks', category: 'retail_meat_seafood' },

    // Retail Veg
    { key: 'retail_veg_onion_1kg', label: 'Onion (1kg)', labelMm: 'ကြက်သွန်နီ (၁ ကီလို)', unit: 'Ks', category: 'retail_fruit_veg' },
    { key: 'retail_veg_garlic_500g', label: 'Garlic (500g)', labelMm: 'ကြက်သွန်ဖြူ (၅၀၀ ဂရမ်)', unit: 'Ks', category: 'retail_fruit_veg' },
    { key: 'retail_veg_tomato_pack', label: 'Cherry Tomato (Pack)', labelMm: 'ခရမ်းချဉ်သီး (၁ ထုပ်)', unit: 'Ks', category: 'retail_fruit_veg' },
    { key: 'retail_veg_cabbage_500g', label: 'Cabbage (500g)', labelMm: 'ဂေါ်ဖီထုပ် (၅၀၀ ဂရမ်)', unit: 'Ks', category: 'retail_fruit_veg' },

    // Retail Pulses
    { key: 'retail_pulse_chickpea_1kg', label: 'Chick Pea (1kg)', labelMm: 'ကုလားပဲ (၁ ကီလို)', unit: 'Ks', category: 'retail_pulses' },

    // Retail Ingredients
    { key: 'retail_ingredient_salt_1kg', label: 'Iodized Salt (1kg)', labelMm: 'အိုင်အိုဒင်းဆား (၁ ကီလို)', unit: 'Ks', category: 'retail_ingredients' },
];

export function getCommoditiesByCategory(category: string): CommodityMeta[] {
    return COMMODITIES.filter((c) => c.category === category);
}

export function formatValue(value: number | null, unit: string): string {
    if (value === null) return 'N/A';

    const formatted = value.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    return `${formatted} ${unit}`;
}
